// /forgot-password — anonymous "send me a reset link" form.
//
// The backend ALWAYS returns 202 regardless of whether the address is
// registered (anti-enumeration). We mirror that here: render the same
// success state no matter what. The user can't tell from this page
// whether their account exists; they only learn the answer by checking
// the inbox.

import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Logo } from "../components/Logo";
import { useI18n } from "../lib/i18n";
import { auth } from "../lib/api";

function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await auth.forgotPassword({ email: email.trim().toLowerCase() });
    } finally {
      setSubmitting(false);
      // Always show success. Even if the backend hiccupped or the
      // email doesn't exist, the user shouldn't be able to tell.
      setSent(true);
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 flex flex-col gap-5">
        <Link to="/login" className="ring-nexus rounded self-start"><Logo size="sm" /></Link>

        {sent ? (
          <>
            <h1 className="font-display text-2xl font-semibold">{t("forgotPassword.sentTitle")}</h1>
            <p className="text-secondary text-sm">{t("forgotPassword.sentBody")}</p>
            <Link to="/login"><Button className="w-full" variant="secondary">{t("forgotPassword.backToLogin")}</Button></Link>
          </>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <h1 className="font-display text-2xl font-semibold">{t("forgotPassword.title")}</h1>
            <p className="text-secondary text-sm">{t("forgotPassword.subtitle")}</p>
            <Input
              label={t("forgotPassword.email")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              autoFocus
            />
            <Button type="submit" loading={submitting} disabled={!email.trim()}>
              {t("forgotPassword.sendLink")}
            </Button>
            <Link to="/login" className="text-secondary text-sm hover:text-primary self-center">
              {t("forgotPassword.backToLogin")}
            </Link>
          </form>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPassword,
});
